import { EmailTextPhrases, EmailTypeEnum, IScheduledEmailContent } from '@/types/email.types';
import { isString } from 'underscore';
import { EmailContent, EmailDesign, EventEmailProps, namePlaceholder } from './event-email';
import { JSX } from 'react';
import { getReminderWording } from '../helpers/get-reminder-wording';
import { TextAlignmentsEnum } from '@/types/template-layouts.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import AddToCalendar from './add-to-calendar';
import useEditableEmailFields from '../helpers/use-editable-email-fields';

export interface EmailContentProps {
  emailType: EmailTypeEnum;
  design: EmailDesign;
  emailContent: EmailContent;
  customContent?: IScheduledEmailContent;
  onEdit?: EventEmailProps['onEdit'];
  sent?: boolean;
  forceRtl?: boolean;
  hideCalendar?: boolean;
}
interface IPlainTextDefaultProps {
  salutation: string;
  reminder: string;
  sessionStart: string;
  sessionName: string;
  sessionDescription: string;
  emailContent: EmailContent;
  emailType: EmailTypeEnum;
  hideCalendar?: boolean;
  seeDetailsText: string;
}

const defaultPlainTextSessionEmail = (data: IPlainTextDefaultProps): string => {
  return `${data.salutation},\n\n${data.reminder}\n\n${data.sessionStart} ${data.sessionName}\n\n${data.sessionDescription}\n\n${data.seeDetailsText}: session_url`;
};

const SessionEmailContent = ({
  emailType,
  design,
  emailContent,
  customContent,
  onEdit,
  sent,
  forceRtl,
  hideCalendar,
}: EmailContentProps): JSX.Element => {
  const textPhrase = (key: keyof Omit<EmailTextPhrases, 'subject'>) => {
    return emailContent.translations[key];
  };

  const days = emailContent.session?.daysToStart ?? 0;
  const reminderWording = getReminderWording(emailType, days, textPhrase);

  const initialButtonText = textPhrase(
    emailType === EmailTypeEnum.session_reminder ? 'SessionDetails' : 'WatchSession'
  );
  const buttonText = customContent?.button && isString(customContent.button) ? customContent.button : initialButtonText;

  interface IEditableContent {
    salutation: JSX.Element;
    reminder: JSX.Element;
    sessionName: JSX.Element;
    sessionDescription: JSX.Element;
  }

  const editableContent: IEditableContent = {
    salutation: (
      <h4 style={design.styles.h4}>
        <span style={design.styles.h4Span}>
          {textPhrase('salutation')} {namePlaceholder},
        </span>
      </h4>
    ),
    reminder: (
      <h3 style={design.styles.h3}>
        <span style={design.styles.h3Span}>
          {emailContent?.session?.name ?? ''} {reminderWording}
        </span>
      </h3>
    ),
    sessionName: (
      <h3 style={design.styles.h3}>
        <span style={design.styles.h3Span}>{emailContent?.session?.name ?? ''}</span>
      </h3>
    ),
    sessionDescription: (
      <p style={design.styles.p}>
        <span style={design.styles.pSpan}>{emailContent?.session?.description ?? ''}</span>
      </p>
    ),
  };

  const { getCellContent, getButton, getImage } = useEditableEmailFields<IEditableContent>({
    onEdit,
    sent,
    design,
    emailContent,
    emailContentType: 'session',
    editableContent,
    customContent,
    buttonText,
  });

  if (!emailContent.session) return <></>;

  const alignment = (ltrLanguageAlign = TextAlignmentsEnum.center) =>
    forceRtl ? TextAlignmentsEnum.right : ltrLanguageAlign;
  const direction = forceRtl ? 'rtl' : 'ltr';

  if (emailContent?.settings?.usePlainTextEmail) {
    const calendarLinkAttachments =
      emailContent.settings?.showAddCalendar &&
      emailType === EmailTypeEnum.session_reminder &&
      emailContent?.session?.icsFileContent?.icsFileText &&
      !hideCalendar
        ? [
            {
              name: 'cal.ics',
              content: emailContent.session.icsFileContent.icsFileText,
              contentId: 'cal',
              contentType: 'text/calendar; charset=utf-8; method=REQUEST',
              disposition: 'inline',
            },
          ]
        : [];

    return (
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          <textarea
            autoFocus // DO NOT REMOVE autoFocus, we need this to initialize the plainTextArea content in working_customizations by triggering onBlur
            rows={33}
            disabled={sent}
            style={{ width: '100%', resize: 'none', whiteSpace: 'pre-wrap' }}
            onBlur={(e) =>
              !sent && onEdit?.(e.target.value, 'plainTextEmail', { attachments: calendarLinkAttachments })
            }
            defaultValue={
              (customContent?.plainTextEmail as string | undefined) ??
              defaultPlainTextSessionEmail({
                salutation: textPhrase('salutation') + ' ' + namePlaceholder,
                reminder: `${emailContent.session.name} ${reminderWording}`,
                sessionStart:
                  !!emailContent.session.start && emailContent.settings?.showStartTime
                    ? emailContent.session.start
                    : '',
                sessionName: emailContent.session.name,
                sessionDescription: emailContent.session.description,
                emailContent,
                emailType,
                hideCalendar,
                seeDetailsText: buttonText,
              })
            }
          />
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          {getCellContent('salutation')}
        </td>
      </tr>
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          {getCellContent('reminder')}
        </td>
      </tr>
      <tr>{getImage()}</tr>
      <OptionalComponent display={!!emailContent.session.start && emailContent.settings?.showStartTime}>
        <tr>
          <td align={alignment()} dir={direction}>
            <p style={{ ...design.styles.accent, ...design.styles.accentSpan }}>{emailContent.session.start}</p>
          </td>
        </tr>
      </OptionalComponent>
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          {getCellContent('sessionName')}
        </td>
      </tr>
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          {getCellContent('sessionDescription')}
        </td>
      </tr>
      <tr style={{ height: '55px' }}>
        <td align={alignment()} dir={direction}>
          {getButton()}
        </td>
      </tr>
      <OptionalComponent
        display={
          emailContent.settings?.showAddCalendar && emailType === EmailTypeEnum.session_reminder && !hideCalendar
        }
      >
        <AddToCalendar
          emailContent={emailContent}
          design={design}
          emailType={emailType}
          customContent={customContent}
          onEdit={onEdit}
          sent={sent}
        />
      </OptionalComponent>
    </>
  );
};

export default SessionEmailContent;
